/**
 * Доля хвоста публичного рендерера: вид `rating_stars` (оценка звёздами).
 *
 * WEB-2920, заход 4: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово. Метка `case 'rating_stars'` остаётся в фасаде; ветка вида — функция
 * отрисовки `renderRatingStars(ctx)`. Замыкания фасада приходят объектом-контекстом
 * (`ExtendedRenderContext`); ответ посетителя ветка читает из `values` и пишет
 * через `setValue` — это `useFormValues()` фасада: хук стоит до `switch`, в
 * функции отрисовки его звать нельзя (рисуется одна ветка за рендер). Эту
 * долю импортирует только фасад: она едет в ленивом куске `extended`, а не в
 * первом экране (самопроверка обхода).
 */
import React from 'react';
import { mutedTextRoleStyleWith, textRoleStyle } from '../semanticTextRoles';
// WEB-916 п.2: цвет закрашенной звезды — токеном (прежний жёлтый давал 1.9:1).
import {
  RATING_STAR_COLOR,
  RATING_STAR_EMPTY_OPACITY,
} from '../ratingStarColor';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';


export function renderRatingStars(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, richText, hasText, values, setValue } = ctx;
  // WEB-1692 юнит 4: ролей у блока НЕТ намеренно — подпись правят
  // СТАРШИЕ общие поля («Размер шрифта (px)» = block.fontSize, тип стоит
  // в INNER_FONT_SIZE_DUPLICATE_BLOCK_TYPES, и «Цвет текста внутри
  // элемента»). Не настраивались только звёзды: кегль 32 числом, цвет
  // константой. Им своя пара по образцу countdownDigit*.
  const starRole = textRoleStyle(
    block.ratingStarColor,
    block.ratingStarFontSize,
  );
  // WEB-691b: stars row alignment — absent/'left' keeps the previous
  // (unaligned flex-start) layout unchanged.
  const starsJustify =
    block.starsAlign === 'center'
      ? 'center'
      : block.starsAlign === 'right'
        ? 'flex-end'
        : 'flex-start';
  return (
    <div style={{ marginBottom: 16 }}>
      {hasText(block.ratingLabel) && (
        <p
          data-testid="rating-stars-label"
          style={{
            fontWeight: 600,
            marginBottom: 12,
            ...(block.fontSize ? { fontSize: block.fontSize } : {}),
          }}
        >
          {richText(block.ratingLabel)}
        </p>
      )}
      <div
        style={{ display: 'flex', gap: 8, justifyContent: starsJustify }}
      >
        {Array.from({ length: block.ratingMax ?? 5 }).map((_, i) => {
          const filled = Number(values[block.id] ?? 0) >= i + 1;
          return (
            <span
              key={i}
              data-testid={`rating-star-${i}`}
              onClick={() => setValue(block.id, String(i + 1))}
              style={{
                fontSize: 32,
                color: filled
                  ? RATING_STAR_COLOR
                  : 'var(--color-text-secondary, #d9d9d9)',
                opacity: filled ? 1 : RATING_STAR_EMPTY_OPACITY,
                cursor: 'pointer',
                // WEB-1692 юнит 4, вариант А: авторский цвет берут ОБЕ
                // звезды, а разницу состояний держит ТА ЖЕ прозрачность
                // 0.5, что стоит здесь сегодня, — второго поля цвета
                // автору не дают, и вид без настройки не меняется.
                ...starRole,
                ...(filled
                  ? {}
                  : mutedTextRoleStyleWith(starRole, RATING_STAR_EMPTY_OPACITY)),
              }}
              role="button"
              aria-label={`${i + 1} star`}
            >
              ★
            </span>
          );
        })}
      </div>
    </div>
  );
}
